import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { ArrowLeft, ArrowRight, Sparkles, Upload, X } from 'lucide-react';
import { mockQuestions } from '../data/mockData';
import { useToast } from '../hooks/use-toast';
import GeneratingAnimation from './GeneratingAnimation';
import PhotoUpload from './PhotoUpload';

const Questionnaire = ({ onComplete, userData }) => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [preferences, setPreferences] = useState({
    length: 'medium', // short, medium, long
    tone: 'warm' // warm, formal, poetic
  });

  const currentQuestion = mockQuestions[currentStep];
  const progress = ((currentStep + 1) / mockQuestions.length) * 100;
  
  // Calculate section progress
  const currentSection = currentQuestion.section;
  const totalSections = 7;
  const questionsInSection = mockQuestions.filter(q => q.section === currentSection).length;
  const currentQuestionInSection = mockQuestions
    .filter(q => q.section === currentSection && q.id <= currentQuestion.id).length;

  // Auto-save progress to localStorage AND backend
  useEffect(() => {
    const savedData = {
      answers,
      currentStep,
      photos: photos.map(p => ({ name: p.name, caption: p.caption, section: p.section, year: p.year })),
      preferences,
      userData
    };
    localStorage.setItem('autobiography_progress', JSON.stringify(savedData));

    // Debounced backend sync
    const email = userData?.email;
    if (email && Object.keys(answers).length > 0) {
      const timer = setTimeout(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/progress/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, answers, current_step: currentStep, preferences }),
        }).catch(() => {});
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [answers, currentStep, preferences]);

  // Load saved progress (try backend first, then localStorage)
  useEffect(() => {
    const loadProgress = async () => {
      const email = userData?.email;
      if (email) {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/progress/load?email=${encodeURIComponent(email)}`);
          if (res.ok) {
            const data = await res.json();
            if (data.found && data.answers && Object.keys(data.answers).length > 0) {
              setAnswers(data.answers);
              setCurrentStep(data.current_step || 0);
              if (data.preferences) setPreferences(data.preferences);
              toast({ title: "Progress Restored", description: "Your previous answers have been loaded." });
              return;
            }
          }
        } catch {}
      }
      // Fallback to localStorage
      const saved = localStorage.getItem('autobiography_progress');
      if (saved) {
        try {
          const data = JSON.parse(saved);
          if (data.userData?.email === userData?.email) {
            setAnswers(data.answers || {});
            setCurrentStep(data.currentStep || 0);
            setPhotos(data.photos || []);
            setPreferences(data.preferences || { length: 'medium', tone: 'warm' });
            toast({ title: "Progress Restored", description: "We've restored your previous answers!" });
          }
        } catch (e) {
          console.error('Error loading saved progress:', e);
        }
      }
    };
    loadProgress();
  }, []);

  const handleAnswer = (value) => {
    setAnswers({
      ...answers,
      [currentQuestion.id]: value
    });
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 3) {
      toast({
        title: "Too Many Photos",
        description: "You can upload up to 3 photos maximum.",
        variant: "destructive"
      });
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          data: event.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (photoId) => {
    setPhotos(photos.filter(p => p.id !== photoId));
  };

  const handleNext = () => {
    if (currentStep < mockQuestions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // After last question, show photo upload
      setShowPhotoUpload(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePhotosContinue = (uploadedPhotos) => {
    setPhotos(uploadedPhotos);
    handleGenerate();
  };

  const handlePhotosSkip = () => {
    setPhotos([]);
    handleGenerate();
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenerationProgress(0);

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.random() * 15;
      });
    }, 1000);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/autobiography/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_name: userData?.name || 'User',
          user_email: userData?.email || 'user@example.com',
          answers: answers,
          photos: photos.map(p => ({ name: p.name, data: p.data, caption: p.caption || '', section: p.section || '', year: p.year || '' })),
          language: { en: 'English', fr: 'French', es: 'Spanish', de: 'German', it: 'Italian', pt: 'Portuguese', ar: 'Arabic', zh: 'Chinese', ja: 'Japanese', hi: 'Hindi' }[localStorage.getItem('i18nextLng') || 'en'] || 'English'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate autobiography');
      }
      
      const data = await response.json();
      
      clearInterval(progressInterval);
      setGenerationProgress(100);
      
      // Small delay to show 100%
      setTimeout(() => {
        // Clear saved progress
        localStorage.removeItem('autobiography_progress');
        onComplete(data);
      }, 500);
      
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Error generating autobiography:', error);
      toast({
        title: "Generation Failed",
        description: "Unable to generate your autobiography. Please try again.",
        variant: "destructive"
      });
      setIsGenerating(false);
    }
  };

  if (isGenerating) {
    return <GeneratingAnimation progress={generationProgress} />;
  }

  // Show photo upload after all questions
  if (showPhotoUpload) {
    return (
      <PhotoUpload
        onContinue={handlePhotosContinue}
        onSkip={handlePhotosSkip}
      />
    );
  }

  // Preferences Step (before questions)
  if (currentStep === 0 && Object.keys(answers).length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 fade-in">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
              Personalize Your Autobiography
            </h2>
            
            <div className="space-y-8">
              {/* Length Selection */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Story Length</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: 'short', label: 'Concise', desc: '~2,000 words' },
                    { value: 'medium', label: 'Standard', desc: '~4,000 words' },
                    { value: 'long', label: 'Detailed', desc: '~6,000 words' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setPreferences({ ...preferences, length: option.value })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        preferences.length === option.value
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <p className="font-semibold text-gray-800">{option.label}</p>
                      <p className="text-sm text-gray-600">{option.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tone Selection */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Writing Tone</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: 'warm', label: 'Warm & Personal', icon: '💖' },
                    { value: 'formal', label: 'Formal & Elegant', icon: '✨' },
                    { value: 'poetic', label: 'Poetic & Lyrical', icon: '🎭' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setPreferences({ ...preferences, tone: option.value })}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        preferences.tone === option.value
                          ? 'border-pink-500 bg-pink-50'
                          : 'border-gray-200 hover:border-pink-300'
                      }`}
                    >
                      <p className="text-2xl mb-2">{option.icon}</p>
                      <p className="font-semibold text-gray-800 text-sm">{option.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Photo Upload */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Add Photos (Optional)
                </h3>
                <p className="text-gray-600 mb-4 text-sm">
                  Upload up to 3 photos to personalize your autobiography
                </p>
                
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">Click to upload photos</p>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                  </label>
                </div>

                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    {photos.map(photo => (
                      <div key={photo.id} className="relative group">
                        <img
                          src={photo.data}
                          alt="Uploaded"
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removePhoto(photo.id)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={() => setCurrentStep(0)}
              className="btn-primary w-full mt-8 text-white py-6 text-lg rounded-full border-0"
            >
              Continue to Questions
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <div>
              <span className="text-sm font-medium text-gray-700">
                {currentQuestion.category}
              </span>
              <span className="text-xs text-gray-500 ml-2">
                ({currentQuestionInSection} of {questionsInSection})
              </span>
            </div>
            <span className="text-sm font-medium text-gray-700">
              Section {currentSection} of {totalSections}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-gray-500 mt-1 text-right">
            Overall: {currentStep + 1} of {mockQuestions.length} questions • {Math.round(progress)}% complete
          </p>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 fade-in">
          {/* Section Badge */}
          <div className="flex items-center gap-2 mb-6">
            <div className="inline-block bg-gradient-to-r from-pink-200 to-purple-200 px-4 py-1 rounded-full">
              <span className="text-sm font-medium text-gray-700">
                {currentQuestion.category}
              </span>
            </div>
            {currentQuestionInSection === 1 && (
              <span className="text-xs text-purple-600 font-medium">
                New Section!
              </span>
            )}
          </div>

          {/* Question */}
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 leading-relaxed">
            {currentQuestion.question}
          </h2>

          {/* Answer Textarea */}
          <Textarea
            placeholder={currentQuestion.placeholder}
            value={answers[currentQuestion.id] || ''}
            onChange={(e) => handleAnswer(e.target.value)}
            className="min-h-[250px] text-lg input-focus resize-none leading-relaxed"
          />

          <div className="mt-3 space-y-2">
            <p className="text-sm text-purple-600 font-medium">
              💡 Take your time — stories and details make your autobiography richer.
            </p>
            <p className="text-xs text-gray-500">
              ✓ Your progress is saved automatically after every question
            </p>
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-4 mt-8">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Previous
            </Button>
            
            <Button
              onClick={handleNext}
              disabled={!answers[currentQuestion.id] || answers[currentQuestion.id].trim() === ''}
              className="btn-primary flex-1 flex items-center justify-center gap-2 text-white border-0"
            >
              {currentStep === mockQuestions.length - 1 ? (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate My Story
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-sm text-gray-600">
            Your progress is automatically saved
          </p>
          <button
            onClick={() => {
              localStorage.removeItem('autobiography_progress');
              toast({
                title: "Progress Cleared",
                description: "Your saved answers have been deleted.",
              });
            }}
            className="text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Clear saved progress
          </button>
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;
